export const routes = {
  adminPanel: '/admin',
  customer: '/customer',
  default: '/',
  guest: '/guest',
  loyaltyLegacy: '/loyalty-legacy',
  intlLoyaltyTransaction: '/intl-loyalty-transaction',
  intlLoyalty: '/intl-loyalty',
  offers: '/offers',
  orders: '/orders',
  permissionPanel: '/permissions',
  history: '/history',
  signIn: '/signin',
  store: '/store',
  supportHistory: '/support-history',
};

export const nestedResources = {
  [routes.customer]: [routes.intlLoyalty, routes.offers, routes.orders, routes.supportHistory].map(
    (resource) => resource.replace('/', ''),
  ),
};

export const isRouteSelected = (route: string, pathName: string) => {
  // This function is not an exact science
  // One day we will migrate to React Router and use https://v5.reactrouter.com/web/api/matchPath
  // Until the this appears to be the best we can do...
  const routeResource = route.replace('/', '');
  const pathParts = pathName.split('/').filter(Boolean);
  const firstPathPart = `/${pathParts[0]}`;
  const lastPathPart = pathParts.at(-1) || '';
  const isCurrentPathNestedResource = !!nestedResources[firstPathPart]?.includes(lastPathPart);
  if (isCurrentPathNestedResource) {
    // we want the offers tab to remain selected
    if (lastPathPart.startsWith('offers')) {
      return routeResource.includes('offers');
    }
    // all other nested resources
    return lastPathPart === routeResource;
  }
  if (pathParts.length === 4) {
    // special case for double nested orders route: customers/:customerId/orders/:orderId
    return pathParts[2] === routeResource;
  }
  if (pathParts.length === 1 || pathParts.length === 2) {
    return firstPathPart === route;
  }
};

const expandOrdersPath = ({
  customerId,
  guestEmail,
  guestRegion,
  orderId,
}: {
  customerId: string;
  guestEmail: string;
  guestRegion: string;
  orderId: string;
}) => {
  if (!!guestEmail && !customerId) {
    const encodedEmail = encodeURIComponent(guestEmail);
    return guestRegion
      ? `${routes.guest}/${encodedEmail}${routes.orders}/region/${encodeURIComponent(guestRegion)}`
      : `${routes.guest}/${encodedEmail}${routes.orders}`;
  }

  if (!!customerId && !orderId) {
    return `${routes.customer}/${customerId}${routes.orders}`;
  }

  if (!!customerId && !!orderId) {
    return `${routes.customer}/${customerId}${routes.orders}/${orderId}`;
  }

  return routes.orders;
};

export const expandPath = (resource: string, params: Record<string, string>) => {
  const { customerId = '', guestEmail = '', guestRegion = '', storeId = '', orderId = '' } = params;
  if (resource === routes.customer && !!customerId) {
    return `${routes.customer}/${customerId}`;
  }
  if (resource === routes.intlLoyalty && !!customerId) {
    return `${routes.customer}/${customerId}${routes.intlLoyalty}`;
  }
  if (resource === routes.orders) {
    return expandOrdersPath({ customerId, guestEmail, guestRegion, orderId });
  }
  if (resource === routes.offers && !!customerId) {
    return `${routes.customer}/${customerId}${routes.offers}`;
  }
  if (resource === routes.supportHistory && !!customerId) {
    return `${routes.customer}/${customerId}${routes.supportHistory}`;
  }
  if (resource === routes.store && !!storeId) {
    return `${routes.store}/${storeId}`;
  }
  return resource;
};
